import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { t, tf } from '../../lib/i18n';
import { recommendFromMetrics, weeklyPick } from '../../lib/articleRecommend';
import { getArticle, tipsPath } from '../../seo/articles';

/** Dashboard card: one guide article picked from the lowest logged metric, or a weekly rotation. */
export const ReadingCard = ({ latestProgress, language }) => {
  const pick = useMemo(() => {
    const fromMetrics = recommendFromMetrics(latestProgress);
    const key = fromMetrics?.key || weeklyPick();
    return { article: getArticle(key, language), fromMetrics };
  }, [latestProgress, language]);
  const { article, fromMetrics } = pick;
  if (!article) return null;
  const reason = fromMetrics
    ? tf('readingReasonMetric', language, { metric: t(fromMetrics.metric, language), value: Math.round(fromMetrics.value) })
    : t('readingReasonWeekly', language);

  return (
    <Card className="bg-lux-panel/90 border-white/[0.08] hover:border-lux-gold/40 transition-[border-color] duration-200 overflow-hidden fade-in" data-testid="dashboard-reading-card">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Link to={article.path} className="md:col-span-4 relative block aspect-[3/2] md:aspect-auto md:min-h-[180px] bg-black/40" aria-label={article.title} data-testid="dashboard-reading-cover">
            <img src={article.cover} alt={article.imageAlt} width="1200" height="800" loading="lazy" className="w-full h-full object-cover md:absolute md:inset-0" />
          </Link>
          <div className="md:col-span-8 p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--lux-gold)] inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {t('readThisWeek', language)}</p>
              <span className="text-[11px] text-white/40">{article.axisName}</span>
            </div>
            <h3 className="text-lg font-semibold text-white leading-snug" data-testid="dashboard-reading-title">{article.title}</h3>
            <p className="text-sm text-white/55 leading-relaxed">{article.excerpt}</p>
            <p className="text-xs text-white/40" data-testid="dashboard-reading-reason">{reason}</p>
            <div className="flex items-center justify-between gap-3 flex-wrap mt-auto pt-1">
              <span className="text-xs text-white/40 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--lux-gold)]" /> {article.minutes} min</span>
              <div className="flex items-center gap-4">
                <Link to={tipsPath(language)} className="text-xs text-white/50 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid="dashboard-reading-all">{t('allTipsLink', language)}</Link>
                <Link to={article.path} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200" data-testid="dashboard-reading-link">
                  {language === 'fr' ? 'Lire' : 'Read'} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
